import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import randomColor from 'randomcolor';
import { RootState } from './store';
import APIService from '../Utils/APIService';
import DraftOperation, { Coordinates } from '../Types/DraftOperation';
import { AddressType } from '../Pages/dashboard/LocationSelector';
import { clearSelectedDriversAndVehicles, getMinimalVehicles, getSelectedVehicles } from './drivers.store';
import { getSocket } from '../Utils/SocketService';
import { DEFAULT_PAGE_SIZE } from '../Utils/constants';
import { TTripData } from '../SharedComponents/Models/AddNewOperation';
import { fetchRoutes } from './trips.store';

type InputLocation = string | AddressType | undefined;

export interface OperationsState {
  addXlslLoading: boolean;
  draftOperations: DraftOperation[];
  groupedDraftOperations: DraftOperation[];
  optimizationProgress: number | null;
  optimizedVehicles: any[];
  fetchingGeoCoding: boolean;
  submitLoading: boolean;
  optimizationResult: any;
}

const initialState: OperationsState = {
  addXlslLoading: false,
  draftOperations: [],
  groupedDraftOperations: [],
  optimizationProgress: null,
  optimizedVehicles: [],
  fetchingGeoCoding: false,
  submitLoading: false,
  optimizationResult: null,
};

const getRandomColor = (index: number) => {
  if (index < 10) {
    return [
      '#F31559',
      '#6528F7',
      '#539165',
      '#F2BE22',
      '#090580',
      '#0079FF',
      '#0079FF',
      '#C07F00',
      '#9A208C',
      '#AA77FF',
    ][index];
  }
  return randomColor({ luminosity: 'light' });
};

function getApiOperation(input: InputLocation, flags: { isStart?: boolean; isEnd?: boolean }): DraftOperation {
  return {
    address: typeof input === 'string' ? input : input?.address || '',
    locationId: 'N/A',
    name: 'N/A',
    waiting_time: null,
    postalCode: 0,
    contact_no: '',
    priority: 0,
    minWindow: null,
    maxWindow: null,
    value: 0,
    items: [],
    isStartingRow: Boolean(flags.isStart),
    isEndingRow: Boolean(flags.isEnd),
    longitude: typeof input === 'string' ? null : Number(input?.lng),
    latitude: typeof input === 'string' ? null : Number(input?.lat),
  };
}

function isSameLocation(operation: any, loc: InputLocation): boolean {
  if (!loc || !operation) {
    return false;
  }
  if (typeof loc === 'string') {
    return operation.address === loc;
  }
  return (
    operation.address === loc.address &&
    Number(operation.location?.lng ?? operation.longitude) === Number(loc.lng) &&
    Number(operation.location?.lat ?? operation.latitude) === Number(loc.lat)
  );
}

function sanitizeLocation(loc: any): any {
  return {
    id: loc.id,
    longitude: loc.longitude,
    latitude: loc.latitude,
    priority: loc.priority ?? null,
    name: loc.name ?? '',
    address: loc.address ?? '',
    postal_code: loc.postal_code ?? '',
    value: Number(loc.value ?? 0),
    waiting_time_minutes: loc.waiting_time_minutes ?? null,
    contact_no: loc.contact_no ?? '',
    items: (loc.items ?? []).map((item: any) => ({
      item_name: item.item_name ?? '',
      capacity: Number(item.capacity ?? 0),
      operation: item.operation ?? 'pickup',
    })),
    ...(loc.isStartingRow || loc.is_start ? { is_start: true } : {}),
    ...(loc.isEndingRow || loc.is_end ? { is_end: true } : {}),
  };
}

export const addRouteManually = createAsyncThunk<any, TTripData>(
  'routes/add-manually',
  async (routeData, { rejectWithValue, dispatch }) => {
    const body = {
      ...routeData,
      locations: routeData.locations.map((value) => ({
        ...value,
        latitude: parseFloat(value.latitude),
        longitude: parseFloat(value.longitude),
      })),
    };
    const [err, data] = await to(
      APIService.post('user/upload-trip-manually/simplified', body, {
        headers: { 'Content-Type': 'application/json' },
      }),
    );
    if (err) {
      toast.error('Something went wrong with data. Please try again!');
      return rejectWithValue(err);
    }
    dispatch(
      fetchRoutes({ start: 0, limit: DEFAULT_PAGE_SIZE, licenseNo: '', startDate: '', endDate: '', tripName: '' }),
    );
    toast.success('Successfully Added the Route');
    return data;
  },
);

export const fetchGeoCodingForDraftOperations = createAsyncThunk<any, any>(
  'operations/geocoding',
  async (_, { rejectWithValue, getState }) => {
    let draftOperations = getDraftOperations(getState());
    if (draftOperations.length === 0) {
      const local = JSON.parse(localStorage.getItem('draft-operations') || '[]');
      if (local.length !== 0) {
        draftOperations = local;
      }
    }
    if (draftOperations.length === 0) return [];
    const request = draftOperations.map(({ key, address, name, locationId, longitude, latitude }) => ({
      address: address || null,
      name: name || null,
      location_id: locationId || null,
      location_key: key,
      longitude: longitude || null,
      latitude: latitude || null,
    }));
    const [err, data] = await to(APIService.post('user/geocoding', { address_arr: request }));
    if (err) return rejectWithValue(err);
    try {
      const resultMap = data!.data.reduce((p: any, c: any) => {
        p[c.key] = c;
        return p;
      }, {});
      return draftOperations.map(({ key, ...rest }) => {
        const resultData = resultMap[key!];
        if (resultData) {
          return {
            ...rest,
            key,
            location: { lng: Number(resultData.lon), lat: Number(resultData.lat) },
            status: resultData.status,
          };
        }
        return { key, ...rest, error: true };
      });
    } catch (e) {
      /* ignore */
    }
    return draftOperations;
  },
);

export const submitDraftOperations = createAsyncThunk<any, any>(
  'operation/submit',
  (args, { rejectWithValue, getState, dispatch }) => {
    const { startRow, endRow, singleLocation, routeType, optimizationType } = args || {};
    const draftOperations = getDraftOperations(getState());
    const selectedVehicles = getSelectedVehicles(getState());
    const resolvedType: string = optimizationType ?? ':straight';
    const isCircular = resolvedType === ':circular' || resolvedType === 'single:circular:priority';

    let operationsApi: DraftOperation[] = draftOperations.map((o) => ({
      address: o.address ?? '',
      locationId: o.locationId ?? 'N/A',
      name: o.name ?? 'N/A',
      waiting_time: o.waiting_time !== undefined ? Number(o.waiting_time) : null,
      postalCode: o.postalCode !== undefined ? Number(o.postalCode) : 0,
      contact_no: o.contact_no !== undefined ? o.contact_no : '',
      value: o.value !== undefined ? Number(o.value) : 1,
      minWindow: o.minWindow !== undefined ? Number(o.minWindow) : null,
      maxWindow: o.maxWindow !== undefined ? Number(o.maxWindow) : null,
      priority: o.priority !== undefined ? Number(o.priority) : 0,
      isStartingRow: false,
      isEndingRow: false,
      latitude: o.location?.lat ?? o.latitude ?? null,
      longitude: o.location?.lng ?? o.longitude ?? null,
      items: (o.items ?? []).map((item: any) => ({
        item_name: item.item_name ?? '',
        capacity: Number(item.capacity ?? 0),
        operation: item.operation ?? 'pickup',
      })),
    }));

    if (isCircular) {
      const depotSource = singleLocation ?? startRow;
      const depotExists = operationsApi.some((o) =>
        typeof depotSource === 'string' ? o.address === depotSource : isSameLocation(o, depotSource),
      );
      if (!depotExists && depotSource) {
        operationsApi.unshift(getApiOperation(depotSource, { isStart: true, isEnd: true }));
      }
    } else {
      if (routeType === 'multi') {
        if (startRow && !operationsApi.some((o) => isSameLocation(o, startRow))) {
          operationsApi.unshift(getApiOperation(startRow, { isStart: true }));
        }
        if (endRow && !operationsApi.some((o) => isSameLocation(o, endRow))) {
          operationsApi.push(getApiOperation(endRow, { isEnd: true }));
        }
      } else if (
        (typeof startRow === 'string' && typeof endRow === 'string' && startRow === endRow) ||
        (typeof startRow === 'object' &&
          typeof endRow === 'object' &&
          startRow &&
          endRow &&
          startRow.address === endRow.address)
      ) {
        if (!operationsApi.some((o) => isSameLocation(o, singleLocation)) && singleLocation) {
          operationsApi.unshift(getApiOperation(singleLocation, { isStart: true, isEnd: true }));
        }
      } else {
        if (startRow && !operationsApi.some((o) => isSameLocation(o, startRow))) {
          operationsApi.unshift(getApiOperation(startRow, { isStart: true }));
        }
        if (endRow && !operationsApi.some((o) => isSameLocation(o, endRow))) {
          operationsApi.push(getApiOperation(endRow, { isEnd: true }));
        }
      }
      operationsApi = operationsApi.map((o) => ({
        ...o,
        isStartingRow: isSameLocation(o, singleLocation) || isSameLocation(o, startRow),
        isEndingRow: isSameLocation(o, singleLocation) || isSameLocation(o, endRow),
      }));
    }

    const selectedVehicleIds = Object.keys(selectedVehicles).filter((k) => selectedVehicles[k] === true);
    const minimalVehicles = getMinimalVehicles(getState());

    const vehicleIds = selectedVehicleIds
      .map((plateNumber) => minimalVehicles.find((v) => v.plateNumber === plateNumber)?.vehicle_id)
      .filter((id): id is number => id !== undefined);

    if (vehicleIds.length === 0) {
      toast.error('Could not resolve vehicle IDs. Please reselect vehicles.');
      return rejectWithValue('vehicle_ids not found');
    }

    const originalAddressMap = operationsApi.reduce((map, o, index) => {
      map[`loc-${index}`] = { address: o.address ?? '', name: o.name ?? '' };
      return map;
    }, {} as Record<string, { address: string; name: string }>);

    const locationFlagMap = operationsApi.reduce((map, o, index) => {
      map[`loc-${index}`] = {
        isStartingRow: o.isStartingRow ?? false,
        isEndingRow: o.isEndingRow ?? false,
      };
      return map;
    }, {} as Record<string, { isStartingRow: boolean; isEndingRow: boolean }>);

    const getPriority = (o: DraftOperation): number | null => {
      if (resolvedType !== 'single:straight:priority' && resolvedType !== 'single:circular:priority') {
        return null;
      }
      const p = Number(o.priority ?? 0);
      return p > 0 ? p : null;
    };

    const buildLocation = (o: DraftOperation, index: number) => ({
      id: `loc-${index}`,
      longitude: String(o.longitude ?? ''),
      latitude: String(o.latitude ?? ''),
      priority: getPriority(o),
      name: o.name ?? '',
      address: o.address ?? '',
      postal_code: String(o.postalCode ?? ''),
      value: Number(o.value ?? 0),
      waiting_time_minutes: o.waiting_time !== undefined ? Number(o.waiting_time) : null,
      contact_no: o.contact_no ?? '',
      items: (o.items ?? []).map((item) => ({
        item_name: item.item_name,
        capacity: Number(item.capacity),
        operation: item.operation,
      })),
    });

    const locations = isCircular
      ? operationsApi.map((o, i) => buildLocation(o, i))
      : operationsApi.map((o, i) => ({
          ...buildLocation(o, i),
          ...(o.isStartingRow && routeType !== 'circular' && routeType !== 'single:straight:priority'
            ? { is_start: true }
            : {}),
          ...(o.isEndingRow && routeType !== 'circular' && routeType !== 'single:straight:priority'
            ? { is_end: true }
            : {}),
        }));

    const depotSource = singleLocation ?? startRow;
    const depotIdx = isCircular
      ? operationsApi.findIndex((o) =>
          typeof depotSource === 'string' ? o.address === depotSource : isSameLocation(o, depotSource),
        )
      : operationsApi.findIndex((o) => o.isStartingRow || o.isEndingRow);

    const request = {
      type: resolvedType,
      vehicle_ids: vehicleIds,
      locations,
      ...(isCircular ? { depot_idx: depotIdx >= 0 ? depotIdx : 0 } : {}),
    };

    return new Promise((resolve, reject) => {
      const socket = getSocket();
      const emitOptimize = () => {
        socket.emit('optimize', request);
      };
      if (socket.connected) {
        emitOptimize();
      } else {
        socket.once('connect', emitOptimize);
        socket.connect();
      }

      socket.on('optimization_progress', ({ percent }: { percent: number }) => {
        dispatch(setOptimizationProgress(percent));
      });

      socket.on('optimization_complete', ({ result }: { result: any }) => {
        if (result?.optimized_routes) {
          result.optimized_routes = result.optimized_routes.map((route: any) => {
            const restored: Record<string, any> = Object.fromEntries(
              Object.entries(route.locations ?? {}).map(([key, loc]: [string, any]) => [
                key,
                {
                  ...loc,
                  address: originalAddressMap[loc.id]?.address ?? loc.address,
                  name: originalAddressMap[loc.id]?.name ?? loc.name,
                  isStartingRow: locationFlagMap[loc.id]?.isStartingRow ?? Boolean(loc.is_start),
                  isEndingRow: locationFlagMap[loc.id]?.isEndingRow ?? Boolean(loc.is_end),
                },
              ]),
            );

            if (!isCircular) {
              const presentIds = new Set(Object.values(restored).map((l: any) => l.id));
              locations
                .filter(
                  (l: any) =>
                    !presentIds.has(l.id) &&
                    (locationFlagMap[l.id]?.isStartingRow || locationFlagMap[l.id]?.isEndingRow),
                )
                .forEach((missing: any, i: number) => {
                  restored[String(Object.keys(restored).length + i)] = {
                    ...missing,
                    address: originalAddressMap[missing.id]?.address ?? missing.address,
                    name: originalAddressMap[missing.id]?.name ?? missing.name,
                    isStartingRow: locationFlagMap[missing.id]?.isStartingRow ?? false,
                    isEndingRow: locationFlagMap[missing.id]?.isEndingRow ?? false,
                  };
                });
            }

            return { ...route, locations: restored };
          });
        }
        socket.off('optimization_progress');
        socket.off('optimization_complete');
        socket.off('optimization_error');
        dispatch(setOptimizationProgress(null));
        dispatch(clearSelectedDriversAndVehicles());
        resolve({ ...result, meta: { resolvedType, singleLocation: singleLocation ?? startRow, originalAddressMap } });
      });

      socket.on('optimization_error', ({ message }: { message: string }) => {
        socket.off('optimization_progress');
        socket.off('optimization_complete');
        socket.off('optimization_error');
        dispatch(setOptimizationProgress(null));
        toast.error(message);
        reject(rejectWithValue(message));
      });
    });
  },
);

export const submitGroupedOperations = createAsyncThunk<any, any>(
  'operation/submit2',
  (_, { rejectWithValue, getState }) => {
    const optimizationResult = getOptimizationResult(getState());
    if (!optimizationResult) {
      toast.error('No optimization result found. Please re-optimize.');
      return rejectWithValue('No optimization result');
    }

    const sanitizedResult = {
      ...optimizationResult,
      optimized_routes: (optimizationResult.optimized_routes ?? []).map((route: any) => ({
        ...route,
        locations: Object.fromEntries(
          Object.entries(route.locations ?? {}).map(([key, loc]: [string, any]) => [key, sanitizeLocation(loc)]),
        ),
      })),
    };

    return new Promise((resolve, reject) => {
      const socket = getSocket();
      const emitSubmit = () => {
        socket.emit('optimization-submit', sanitizedResult);
      };
      if (socket.connected) {
        emitSubmit();
      } else {
        socket.once('connect', emitSubmit);
        socket.connect();
      }

      socket.on('optimization_submit_result', (result: any) => {
        socket.off('optimization_submit_result');
        socket.off('optimization_error');
        localStorage.removeItem('draft-operations');
        resolve(result);
      });

      socket.on('optimization_error', ({ message }: { message: string }) => {
        socket.off('optimization_submit_result');
        socket.off('optimization_error');
        toast.error(message);
        reject(rejectWithValue(message));
      });
    });
  },
);

export const operationsSlice = createSlice({
  name: 'operations',
  initialState,
  reducers: {
    setDraftOperations: (state, { payload: { operations, minimalLocations } }) => {
      const locationIdMap = minimalLocations.reduce((p: any, c: any) => {
        p[c.location_id.toString()] = c;
        return p;
      }, {});
      const locationNameMap = minimalLocations.reduce((p: any, c: any) => {
        p[c.location_name && c.location_name.toLowerCase()] = c;
        return p;
      }, {});
      state.draftOperations = operations.map((data: any) => {
        try {
          if (data.Address) {
            data.LocationName = 'N/A';
            data.LocationID = 'N/A';
          } else if (data.LocationID && locationIdMap[data.LocationID]) {
            data.LocationName = locationIdMap[data.LocationID].location_name;
            data.Address = locationIdMap[data.LocationID].address;
          } else if (data.LocationName && locationNameMap[data.LocationName.toLowerCase()]) {
            data.LocationID = locationNameMap[data.LocationName.toLowerCase()].location_id;
            data.Address = locationNameMap[data.LocationName.toLowerCase()].address;
          }
        } catch (e) {
          /* ignore */
        }
        const parseItems = (): any[] => {
          try {
            if (!data.Items) {
              return [];
            }
            if (Array.isArray(data.Items)) {
              return data.Items;
            }
            return JSON.parse(data.Items);
          } catch (e) {
            return [];
          }
        };
        return {
          address: data.Address,
          locationId: data.LocationID,
          name: data.LocationName,
          waiting_time: data.WaitingTime,
          postalCode: data.PostalCode,
          contact_no: data.ContactNo,
          priority: data.PriorityRoutes,
          value: data.Value,
          minWindow: data.MinWindow,
          maxWindow: data.MaxWindow,
          isStartingRow: false,
          isEndingRow: false,
          key: data.key,
          items: parseItems(),
        };
      });
    },
    updateDraftOperation: (state, { payload }) => {
      const { index, key, data, clearErrors } = payload;
      const i = state.draftOperations.findIndex((o) => index === o.key);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.draftOperations[i][key] = data;
      if (clearErrors) state.draftOperations[i].status = 'OK';
    },
    updateGroupedDraftOperations: (state, { payload }) => {
      const { index, key, data } = payload;
      const i = state.groupedDraftOperations.findIndex((o) => index === o.key);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.groupedDraftOperations[i][key] = data;
    },
    clearError: (state, { payload: { index } }) => {
      const i = state.draftOperations.findIndex((o) => index === o.key);
      state.draftOperations[i].status = 'OK';
    },
    addDraftOperation: (state, { payload }) => {
      const {
        address,
        locationId,
        name,
        waiting_time,
        value,
        location,
        priority,
        postalCode,
        contact_no,
        minWindow,
        maxWindow,
        isEndingRow,
        isStartingRow,
        longitude,
        latitude,
        items,
      } = payload;
      state.draftOperations = [
        ...state.draftOperations,
        {
          address,
          locationId,
          location,
          longitude,
          latitude,
          name,
          waiting_time,
          postalCode: postalCode ?? 0,
          contact_no: contact_no ?? '',
          priority: priority ?? 0,
          value: value ?? 0,
          minWindow: minWindow ?? null,
          maxWindow: maxWindow ?? null,
          isStartingRow,
          isEndingRow,
          status: 'OK',
          items: items ?? [],
        },
      ].map((data, index) => ({ ...data, key: `${index + 1}` }));
    },
    addGroupedDraftOperation: (state, { payload }) => {
      const {
        address,
        locationId,
        name,
        waiting_time,
        value,
        items,
        location,
        plateNumber,
        priority,
        isStartingRow,
        isEndingRow,
        minWindow,
        maxWindow,
        latitude,
        longitude,
        contact_no,
      } = payload;
      state.groupedDraftOperations = [
        ...state.groupedDraftOperations,
        {
          address,
          locationId,
          location,
          longitude,
          latitude,
          name,
          waiting_time,
          priority,
          value,
          items: items ?? [],
          minWindow,
          maxWindow,
          isStartingRow,
          isEndingRow,
          status: 'OK',
          plateNumber,
          contact_no: contact_no ?? '',
        },
      ].map((data, index) => ({ ...data, key: `${index + 1}` }));
    },
    removeDraftOperation: (state, { payload }) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.draftOperations = state.draftOperations
        .filter((d) => d.key !== payload)
        .map((d, i) => ({ ...d, key: `${i + 1}` }));
    },
    removeGroupedDraftOperation: (state, { payload }) => {
      state.groupedDraftOperations = state.groupedDraftOperations
        .filter((d) => d.key !== payload)
        .map((d, i) => ({ ...d, key: `${i + 1}` }));
    },
    removeGroup: (state, { payload }) => {
      state.groupedDraftOperations = state.groupedDraftOperations
        .filter((d) => d.plateNumber !== payload)
        .map((d, i) => ({ ...d, key: `${i + 1}` }));
    },
    clearDraftOperations: (state) => {
      state.draftOperations = [];
    },
    setOptimizationProgress: (state, { payload }: { payload: number | null }) => {
      state.optimizationProgress = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGeoCodingForDraftOperations.pending, (s) => {
      s.fetchingGeoCoding = true;
    });
    builder.addCase(fetchGeoCodingForDraftOperations.rejected, (s) => {
      s.fetchingGeoCoding = false;
    });
    builder.addCase(fetchGeoCodingForDraftOperations.fulfilled, (s, { payload }) => {
      s.draftOperations = payload;
      s.fetchingGeoCoding = false;
    });

    builder.addCase(submitDraftOperations.pending, (s) => {
      s.submitLoading = true;
    });
    builder.addCase(submitDraftOperations.rejected, (s) => {
      s.submitLoading = false;
    });
    builder.addCase(submitDraftOperations.fulfilled, (s, { payload }) => {
      s.submitLoading = false;
      const resolvedType: string = payload?.meta?.resolvedType ?? ':straight';
      const singleLocation: any = payload?.meta?.singleLocation;
      const originalAddressMap: Record<string, { address: string; name: string }> =
        payload?.meta?.originalAddressMap ?? {};
      const isCircular = resolvedType === ':circular' || resolvedType === 'single:circular:priority';

      const resultWithoutMeta = { ...payload };
      delete resultWithoutMeta.meta;
      s.optimizationResult = resultWithoutMeta;

      const optimizedRoutes: any[] = payload?.optimized_routes ?? [];
      if (!Array.isArray(optimizedRoutes) || optimizedRoutes.length === 0) {
        toast.error('No optimized routes returned. Please try again.');
        return;
      }

      const groupColors = optimizedRoutes.reduce((p: any, c: any, index: number) => {
        const key = String(c.vehicle_id);
        if (!p[key]) {
          p[key] = getRandomColor(index);
        }
        return p;
      }, {});

      s.groupedDraftOperations = optimizedRoutes.flatMap((route: any) => {
        const vehicleKey = String(route.vehicle_id);
        const locationsArray = Object.entries(route.locations ?? {})
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([, loc]) => loc) as any[];

        let finalLocations = locationsArray;

        if (isCircular) {
          const seenIds = new Set<string>();
          const deduped = locationsArray.filter((loc: any) => {
            if (seenIds.has(loc.id)) {
              return false;
            }
            seenIds.add(loc.id);
            return true;
          });

          let foundDepotIdx = deduped.findIndex((loc: any) => {
            const fullAddress = originalAddressMap[loc.id]?.address ?? loc.address ?? '';
            return typeof singleLocation === 'string' ? fullAddress === singleLocation : false;
          });

          if (foundDepotIdx === -1) {
            foundDepotIdx = 0;
          }

          const depot = deduped[foundDepotIdx];
          const rest = deduped.filter((_: any, i: number) => i !== foundDepotIdx);
          finalLocations = [
            { ...depot, isStartingRow: true, isEndingRow: false },
            ...rest.map((l: any) => ({ ...l, isStartingRow: false, isEndingRow: false })),
            { ...depot, isStartingRow: false, isEndingRow: true },
          ];
        }

        return finalLocations.map((loc: any, index: number) => ({
          error: false,
          address: originalAddressMap[loc.id]?.address ?? loc.address ?? '',
          locationId: loc.id ?? '',
          plateNumber: vehicleKey,
          wayPointIndex: index,
          name: originalAddressMap[loc.id]?.name ?? loc.name ?? '',
          items: loc.items ?? [],
          location: {
            lng: Number(loc.longitude ?? 0),
            lat: Number(loc.latitude ?? 0),
            mapColor: groupColors[vehicleKey],
          },
          postalCode: loc.postal_code ?? '',
          contact_no: loc.contact_no ?? '',
          priority: Number(loc.priority ?? 0),
          value: Number(loc.value ?? 0),
          waiting_time: loc.waiting_time_minutes ?? null,
          minWindow: null,
          maxWindow: null,
          isStartingRow: loc.isStartingRow ?? false,
          isEndingRow: loc.isEndingRow ?? false,
          status: 'OK',
          key: `${index + 1}`,
        }));
      });

      s.optimizedVehicles = optimizedRoutes.map((r: any) => String(r.vehicle_id));
    });
    builder.addCase(submitGroupedOperations.pending, (s) => {
      s.submitLoading = true;
    });
    builder.addCase(submitGroupedOperations.rejected, (s) => {
      s.submitLoading = false;
    });
    builder.addCase(submitGroupedOperations.fulfilled, (s) => {
      s.draftOperations = [];
      s.groupedDraftOperations = [];
    });
  },
});

export const {
  setDraftOperations,
  updateDraftOperation,
  removeDraftOperation,
  addDraftOperation,
  clearDraftOperations,
  clearError,
  updateGroupedDraftOperations,
  addGroupedDraftOperation,
  removeGroupedDraftOperation,
  removeGroup,
  setOptimizationProgress,
} = operationsSlice.actions;

const operationsStore = (store: RootState): OperationsState => store.operations;
export const getExcelUploading = createSelector(operationsStore, (s) => s.addXlslLoading);
export const getOperationSubmitLoading = createSelector(operationsStore, (s) => s.submitLoading);
export const getOptimizedVehicles = createSelector(operationsStore, (s) => s.optimizedVehicles);
export const isGeoCodingFetching = createSelector(operationsStore, (s) => s.fetchingGeoCoding);
export const getOptimizationProgress = createSelector(operationsStore, (s) => s.optimizationProgress);
export const getOptimizationResult = createSelector(operationsStore, (s) => s.optimizationResult);
export const getGroupedDraftOperation = createSelector(operationsStore, (s) => s.groupedDraftOperations);

export const getDraftOperations = createSelector(operationsStore, (s) =>
  s.draftOperations.map((d) => ({
    ...d,
    error: !d.location || !d.location.lat || !d.location.lng || d.status !== 'OK',
  })),
);

export const getGroupedDraftOperations = createSelector(operationsStore, getMinimalVehicles, (s, vehicles) => {
  const grouped = s.groupedDraftOperations.reduce((p, c) => {
    if (p[c.plateNumber!]) p[c.plateNumber!].push(c);
    else p[c.plateNumber!] = [c];
    return p;
  }, {} as { [key: string]: DraftOperation[] });

  const optimizedRoutes = s.optimizationResult?.optimized_routes ?? [];

  return Object.keys(grouped).map((key) => {
    const children = grouped[key];
    const matchedVehicle = vehicles.find((a) => String(a.vehicle_id) === key || a.plateNumber === key);
    const routeInfo = optimizedRoutes.find((route: any) => String(route.vehicle_id) === key);

    return {
      address: matchedVehicle ? `${matchedVehicle.plateNumber}` : key,
      children,
      routeDistance: routeInfo?.route_distance ?? 0,
      initialLoad: routeInfo?.initial_load ?? {},
      routeLoad: routeInfo?.route_load ?? 0,
      value: '',
      type: 'parent',
      readonlyRow: true,
      parentCapacity: matchedVehicle?.capacity ?? 0,
      mapColor: children[0]?.location?.mapColor,
      key,
    };
  });
});

export const getDraftOperationSummary = createSelector(getDraftOperations, (operations) => {
  const summary = { errors: 0, capacity: 0, locations: 0, allLocations: [] as Coordinates[] };
  operations.forEach((o) => {
    summary.locations += 1;
    summary.errors += o.error ? 1 : 0;
    summary.allLocations.push(o.location!);
  });
  return summary;
});

export default operationsSlice.reducer;
